// frontend/src/features/agency/ActiveDriverList.js
// This React component renders an Active drivers list for the current agency—loading from the API, syncing live via Socket.IO, and letting users add, edit, reserve, or delete drivers while keeping Redux state updated.
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  upsertDrivers,
  upsertDriver,
  removeDriver,
  selectDriversByStatus,
  applyStatusChange,          // ✅ from driverSlice thunk we added
} from '../../features/drivers/driverSlice';
// ❌ These counters create a second source of truth for counts. Remove them.
// import { incrementActiveCount, decrementActiveCount } from '../agencies/agenciesSlice';

import { fetchDrivers, addDriver } from '../../services/driverService';
import axios from 'axios';
import socket from '../../services/socket';
import DriverForm from '../../features/drivers/DriverForm';
import DriverEditModal from '../../features/drivers/DriverEditModal';
import { Edit2, UserCheck, Trash2 } from 'lucide-react';
import { selectAgencyIdForUser } from '../auth/authSlice';

export default function ActiveDriverList() {
  const dispatch = useDispatch();
  const agencyId = useSelector(selectAgencyIdForUser);
  console.log('ActiveDriverList agencyId →', agencyId);

  // 1️⃣ All Active drivers in the slice (server scopes to this agency)
  const drivers = useSelector(selectDriversByStatus('Active'));

  // 2️⃣ Local UI state
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  // 3️⃣ Initial load — non-destructive
  useEffect(() => {
    if (!agencyId) return;
    (async () => {
      try {
        const data = await fetchDrivers({ status: 'Active' });
        dispatch(upsertDrivers(data));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [dispatch, agencyId]);

  // 4️⃣ Real‐time updates: always upsert; only remove on actual deletion
  useEffect(() => {
    if (!agencyId) return;

    const sameAgency = d => Number(d.agency_id) === Number(agencyId);

    const handleAdded = d => {
      if (sameAgency(d)) dispatch(upsertDriver(d));
    };

    const handleUpdated = d => {
      if (sameAgency(d)) dispatch(upsertDriver(d));
    };

    const handleDeleted = ({ id }) => {
      dispatch(removeDriver(id)); // real deletion only
    };

    const handleBanned = payload => {
      const id = payload.id ?? payload.driver_id;
      dispatch(applyStatusChange({ id, status: 'Banned' })); // status flip, not removal
    };

    const handleUnbanned = payload => {
      const id = payload.id ?? payload.driver_id;
      // If your flow returns to Reserved after unban, use 'Reserved'
      dispatch(applyStatusChange({ id, status: 'Active' }));
    };

    socket.on('driverAdded',    handleAdded);
    socket.on('driverUpdated',  handleUpdated);
    socket.on('driverDeleted',  handleDeleted);
    socket.on('driverBanned',   handleBanned);
    socket.on('driverUnbanned', handleUnbanned);

    return () => {
      socket.off('driverAdded',    handleAdded);
      socket.off('driverUpdated',  handleUpdated);
      socket.off('driverDeleted',  handleDeleted);
      socket.off('driverBanned',   handleBanned);
      socket.off('driverUnbanned', handleUnbanned);
    };
  }, [dispatch, agencyId]);

  // 5️⃣ Sort for display
  const sorted = useMemo(() => [...drivers], [drivers]);

  // 6️⃣ Handlers
  const handleCreate = async formData => {
    try {
      const d = await addDriver({ ...formData, status: 'Active' });
      dispatch(upsertDriver(d)); // upsert only
      setShowAdd(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleReserve = async id => {
    try {
      const res = await axios.put(`/agencies/drivers/${id}`, { status: 'Reserved' });
      const updated = res.data || res;
      // ✅ Status flip → upsert, do NOT remove
      dispatch(upsertDriver(updated));
      // or: dispatch(applyStatusChange({ id, status: 'Reserved' }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteClick = async id => {
    try {
      await axios.delete(`/agencies/drivers/${id}`);
      dispatch(removeDriver(id)); // ✅ actual deletion
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      {loading ? (
        <div className="flex justify-center items-center h-64">Loading…</div>
      ) : (
        <>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowAdd(true)}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition"
            >
              Add Driver
            </button>
          </div>

          {showAdd && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              onClick={() => setShowAdd(false)}
            >
              <div onClick={e => e.stopPropagation()}>
                <DriverForm
                  initialStatus="Active"
                  agencyId={agencyId}
                  onSave={handleCreate}
                  onClose={() => setShowAdd(false)}
                />
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  {['Name', 'Vehicle Reg.', 'Status', 'Actions'].map(col => (
                    <th key={col} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sorted.map(driver => (
                  <tr key={driver.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm flex items-center">
                      {driver.name}
                      {driver.status === 'Banned' && (
                        <span className="ml-2 px-1.5 py-0.5 text-xs font-semibold text-red-700 bg-red-100 rounded">
                          Banned
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {driver.vehicle_registration_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {driver.status}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => setSelectedDriver(driver)}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleReserve(driver.id)}
                          className="p-1 hover:bg-gray-100 rounded text-yellow-600"
                          title="Reserve"
                        >
                          <UserCheck size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(driver.id)}
                          className="p-1 hover:bg-gray-100 rounded text-red-600"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedDriver && (
            <DriverEditModal
              driver={selectedDriver}
              onClose={() => setSelectedDriver(null)}
              onUpdate={() => setSelectedDriver(null)}
            />
          )}
        </>
      )}
    </>
  );
}
